import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { getCountryConfig } from '../lib/country-config'
import GlobalAuditTimeline from '../components/AuditTimeline'
import type { Document, AuditEntry } from '../types'
import { Tour } from '../components/Tour'

const statusColors: Record<string, string> = {
  PENDING_VIEW: 'bg-yellow-100 text-yellow-800',
  PENDING_SELFIE: 'bg-blue-100 text-blue-700',
  PENDING_PAYMENT: 'bg-orange-100 text-orange-700',
  PENDING_SIGNATURE: 'bg-indigo-100 text-indigo-700',
  SIGNED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-red-100 text-red-700',
}

const docStatusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

const stepOrder = ['PENDING_VIEW', 'PENDING_SELFIE', 'PENDING_PAYMENT', 'PENDING_SIGNATURE', 'SIGNED']



export default function DocumentStatus() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const [doc, setDoc] = useState<Document | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [sendingEmail, setSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  useEffect(() => {
    async function fetchDoc() {
      try {
        const data = await api.get<Document>(`/api/documents/${id}`)
        setDoc(data)
      } catch {
        setError('Failed to load document.')
      } finally {
        setLoading(false)
      }
    }
    fetchDoc()
  }, [id])

  function getSignUrl(tokenAcesso: string): string {
    return `${window.location.origin}/sign/${tokenAcesso}`
  }

  function copySignLink(tokenAcesso: string) {
    navigator.clipboard.writeText(getSignUrl(tokenAcesso)).then(() => {
      setCopiedToken(tokenAcesso)
      setTimeout(() => setCopiedToken(null), 2000)
    })
  }

  function sendWhatsApp(signer: { nome: string; telefone?: string; tokenAcesso?: string }) {
    if (!signer.tokenAcesso) return
    const url = getSignUrl(signer.tokenAcesso)
    const message = encodeURIComponent(
      `${t('docStatus.waMessage', 'Olá')} ${signer.nome},\n\n` +
      `${t('docStatus.waBody', 'Você tem um documento para assinar na plataforma PAKT')}:\n\n` +
      `📄 ${doc?.nome || 'Documento'}\n\n` +
      `${t('docStatus.waLink', 'Acesse o link para assinar')}:\n${url}\n\n` +
      `${t('docStatus.waFooter', 'Enviado via PAKT — pakt.io')}`
    )
    // If phone number available, open chat with that number
    // Otherwise, open WhatsApp with pre-filled message (user picks contact)
    if (signer.telefone) {
      const phone = signer.telefone.replace(/[^0-9+]/g, '').replace('+', '')
      window.open(`https://wa.me/${phone}?text=${message}`, '_blank')
    } else {
      window.open(`https://wa.me/?text=${message}`, '_blank')
    }
  }

  function sendAllWhatsApp() {
    if (!doc?.signers) return
    // Open WhatsApp for each signer sequentially (small delay between each)
    doc.signers.forEach((signer, i) => {
      if (signer.status === 'SIGNED' || signer.status === 'REJECTED') return
      setTimeout(() => sendWhatsApp(signer as any), i * 500)
    })
  }

  async function sendAllEmails() {
    if (!doc) return
    setSendingEmail(true)
    try {
      await api.post(`/api/documents/${doc.id}/send`)
      setEmailSent(true)
      setTimeout(() => setEmailSent(false), 3000)
    } catch {
      setError(t('docStatus.emailError', 'Falha ao enviar e-mails. Tente novamente.'))
    } finally {
      setSendingEmail(false)
    }
  }

  function getStepIndex(status: string): number {
    const idx = stepOrder.indexOf(status)
    return idx >= 0 ? idx : 0
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">{error || 'Document not found.'}</p>
          <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-800 font-medium">
            Back to Dashboard
          </Link>
        </div>
      </div>
    )
  }

  const country = getCountryConfig(doc.countryCode)

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard" className="text-xl font-bold text-indigo-600">
            PAKT
          </Link>
          <Link to="/dashboard" className="text-sm text-gray-600 hover:text-gray-900">
            &larr; {t('common.back', 'Back to Dashboard')}
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Document Info */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{doc.nome}</h1>
              <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                {country && <span>{country.flag} {country.nameEn}</span>}
                <span>Created {new Date(doc.createdAt).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 text-sm font-semibold rounded-full ${docStatusColors[doc.status] || 'bg-gray-100 text-gray-700'}`}
              >
                {t(`dashboard.status.${doc.status}`, doc.status)}
              </span>
            </div>
          </div>

          {doc.codigoVerificacao && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg" data-tour="verify-code">
              <span className="text-xs font-medium text-gray-500">Verification Code:</span>
              <span className="ml-2 font-mono text-sm font-semibold text-gray-900">
                {doc.codigoVerificacao}
              </span>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            {doc.status === 'COMPLETED' && (
              <>
                <button onClick={async () => {
                  try {
                    const token = localStorage.getItem('pakt_token')
                    const res = await fetch(`/api/documents/${doc.id}/download?type=final`, {
                      headers: { Authorization: `Bearer ${token}` }
                    })
                    if (!res.ok) throw new Error('Download failed')
                    const blob = await res.blob()
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${doc.nome}_assinado.pdf`
                    a.click()
                    URL.revokeObjectURL(url)
                  } catch {
                    alert('Erro ao baixar o arquivo. Tente novamente.')
                  }
                }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  {t('docStatus.downloadSigned', 'Baixar PDF assinado')}
                </button>
                <button onClick={async () => {
                  try {
                    const token = localStorage.getItem('pakt_token')
                    const res = await fetch(`/api/documents/${doc.id}/download-zip`, {
                      headers: { Authorization: `Bearer ${token}` }
                    })
                    if (!res.ok) throw new Error('Download failed')
                    const blob = await res.blob()
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${doc.nome}_pakt.zip`
                    a.click()
                    URL.revokeObjectURL(url)
                  } catch {
                    alert('Erro ao baixar o arquivo. Tente novamente.')
                  }
                }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  {t('docStatus.downloadZip', 'Baixar ZIP (PDF + original)')}
                </button>
                <button onClick={() => {
                  const url = `${window.location.origin}/verify/${doc.codigoVerificacao}`
                  const msg = encodeURIComponent(`Documento assinado "${doc.nome}" via PAKT:\n\nVerifique: ${url}`)
                  window.open(`https://wa.me/?text=${msg}`, '_blank')
                }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {t('docStatus.shareCompleted', 'Compartilhar via WhatsApp')}
                </button>
              </>
            )}
            {doc.status === 'DRAFT' && (
              <button onClick={sendAllEmails} disabled={sendingEmail}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
                {sendingEmail ? t('docStatus.sending', 'Enviando...') : emailSent ? t('docStatus.sent', 'Enviado!') : t('docStatus.sendForSigning', 'Enviar para assinatura')}
              </button>
            )}
            {doc.pdfFinalUrl && doc.status === 'COMPLETED' && (
              <a
                href={doc.pdfFinalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Download Signed PDF
              </a>
            )}
          </div>
        </div>

        {/* Signers */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('docStatus.signers', 'Signatários')}
            </h2>
            {doc.status !== 'COMPLETED' && doc.status !== 'CANCELLED' && (
              <div className="flex flex-wrap gap-2">
                <button onClick={sendAllEmails} disabled={sendingEmail}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 disabled:opacity-50 transition">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  {sendingEmail ? t('docStatus.sending', 'Enviando...') : emailSent ? t('docStatus.sent', 'Enviado!') : t('docStatus.sendAllEmail', 'Enviar e-mail para todos')}
                </button>
                <button onClick={sendAllWhatsApp}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  {t('docStatus.sendAllWhatsApp', 'Enviar WhatsApp para todos')}
                </button>
              </div>
            )}
          </div>
          <div className="space-y-4" data-tour="signers-progress">
            {doc.signers?.map((signer) => {
              const signerCountry = getCountryConfig(signer.countryCode)
              const currentStep = getStepIndex(signer.status)
              const steps = ['View', 'Selfie', 'Payment', 'Sign']

              return (
                <div key={signer.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{signer.nome}</span>
                        {signerCountry && <span className="text-sm">{signerCountry.flag}</span>}
                        <span
                          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusColors[signer.status] || 'bg-gray-100 text-gray-700'}`}
                        >
                          {signer.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{signer.email}</p>
                      {signer.signedAt && (
                        <p className="text-xs text-green-600 mt-1">
                          Signed at {new Date(signer.signedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => copySignLink(signer.tokenAcesso)}
                        title={t('docStatus.copyLink', 'Copiar link')}
                        className="px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        {copiedToken === signer.tokenAcesso ? '✓' : t('docStatus.copy', 'Copiar')}
                      </button>
                      <button onClick={() => sendWhatsApp(signer)}
                        title={t('docStatus.sendWhatsApp', 'Enviar por WhatsApp')}
                        className="px-2.5 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition">
                        WhatsApp
                      </button>
                    </div>
                  </div>

                  {/* Step progress */}
                  <div className="mt-3 flex items-center gap-1">
                    {steps.map((step, i) => {
                      // Map step index: View=1, Selfie=2, Payment=3, Sign=4
                      const stepIdx = i + 1
                      const completed = currentStep > stepIdx
                      const active = currentStep === stepIdx
                      return (
                        <div key={step} className="flex items-center gap-1 flex-1">
                          <div
                            className={`h-1.5 flex-1 rounded-full ${
                              completed
                                ? 'bg-green-500'
                                : active
                                  ? 'bg-indigo-500'
                                  : 'bg-gray-200'
                            }`}
                          />
                          <span
                            className={`text-xs ${
                              completed
                                ? 'text-green-600'
                                : active
                                  ? 'text-indigo-600 font-medium'
                                  : 'text-gray-400'
                            }`}
                          >
                            {step}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Audit Timeline */}
        {doc.auditLogs && doc.auditLogs.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('docStatus.audit', 'Audit Trail')}
            </h2>
            <GlobalAuditTimeline events={doc.auditLogs.map(e => ({
                  evento: e.evento,
                  createdAt: e.createdAt || e.timestamp || '',
                  signerId: e.signerId,
                  signerName: e.signerId ? doc.signers?.find((s: any) => s.id === e.signerId)?.nome : undefined,
                  metadataJson: e.metadataJson,
                }))} />
          </div>
        )}
      </main>

      {/* Tour pós-envio do PRIMEIRO documento. Só aparece se o user
          completou os 2 tours anteriores (Dashboard + NewDocument) e
          nunca completou ESTE. Mostra: progresso dos signatários,
          código de verificação, ações principais. */}
      {doc &&
       localStorage.getItem('pakt_tour_first_doc_newdoc') === '1' && (
        <Tour
          storageKey="pakt_tour_first_doc_status"
          steps={[
            {
              targetSelector: '[data-tour="signers-progress"]',
              title: 'Acompanhe os signatários',
              body: 'Cada linha mostra o status do signatário no fluxo (View → Selfie → Pagamento → Assinatura). Cores indicam progresso. Você pode copiar o link ou reenviar por WhatsApp/e-mail.',
              placement: 'top',
            },
            {
              targetSelector: '[data-tour="verify-code"]',
              title: 'Código de verificação público',
              body: 'Esse código permite que QUALQUER pessoa (auditor, juízo, contraparte) confira a autenticidade em /verify/CODIGO — sem precisar de login.',
              placement: 'bottom',
              nextLabel: 'Entendi, fechar',
            },
          ]}
        />
      )}
    </div>
  )
}
